import { ProductFilter } from '@@types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useMediaQuery } from 'usehooks-ts'

import { 
  ButtonProductFilters,
  useSearchProducts 
} from '@entities/Product'

import { useSearchQuery } from '@features/search'
import useSearchModal from '@features/search/hooks/useSearchModal'
import { useAddressStore } from '@features/shipping-methods'

import { ProductCard } from '@widgets/product-preview'

import { QUERY_NAMES } from '@shared/contants'
import { useFilters } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import { DynamicFilters } from '@shared/types'
import CardSkeleton from '@shared/ui/card-skeleton'
import TitleWithCount from '@shared/ui/title-with-count'
import getNoun from '@shared/uttils/getNoun'
import { mapFiltersToApi } from '@shared/uttils/mapFiltersToApi'

import EmptySearch from './EmptySearch'

import styles from './styles.module.scss'

const LIMIT = 12

function ProductsLoading () {
  return (
    <div className={styles.root}>
      {Array.from({ length: 8 }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  )
}

interface SearchProductsProps {
  className?: string;
  classNameEmpty?: string;
  onGetFilters: (filters: ProductFilter[] | undefined) => void;
}

export default function SearchProducts (props: SearchProductsProps) {
  const { closeSearchModal } = useSearchModal()
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { className, onGetFilters } = props
  const { viewItemList } = useAnalytics()
  const trackedSearchRef = useRef<string | null>(null)
  const { filters } = useFilters<DynamicFilters>({
    groupKey: QUERY_NAMES.PRODUCT_FILTERS,
  })
  const apiFilters = mapFiltersToApi(filters)
  const searchQuery = useSearchQuery()
  const { t: tp } = useTranslation('product')
  const { t: ts } = useTranslation('search')
  const [ availableFilters, setAvailableFilters ] = useState<ProductFilter[]>()
  const { shopId } = useAddressStore()

  const { products, pageInfo, filters: queryFilters, loading, fetchNextPage, loadingMore, hasMore } = useSearchProducts({
    shop_id: shopId,
    page: 1,
    limit: LIMIT,
    searchQuery: {
      search: searchQuery,
      filters: {},
    },
    filter: apiFilters,
    skip: !searchQuery || !shopId,
  })

  useEffect(() => {
    if (queryFilters) {
      onGetFilters(queryFilters)
      setAvailableFilters(queryFilters)
    }
  }, [ queryFilters ])

  useEffect(() => {
    if (!isTablet) {
      closeSearchModal()
    }
  }, [ isTablet ])

  useEffect(() => {
    if (products.length > 0 && searchQuery && trackedSearchRef.current !== searchQuery) {
      trackedSearchRef.current = searchQuery
      viewItemList(products, `Search: ${searchQuery}`, 'search_results')
    }
  }, [ products, searchQuery ])

  if (!loading && products.length === 0 && pageInfo?.total === 0) {
    return <EmptySearch />
  }

  return (
    <div className={cn(styles.wrapper)}>
      {searchQuery && (
        <TitleWithCount
          className={styles.title}
          title={ts('notEmpty.title', {
            query: searchQuery,
          })}
          key={`${searchQuery}-${products.length}-${pageInfo?.total}`}
        >
          {apiFilters?.and?.length
            ? `${products.length} ${getNoun(products.length, tp('product.1'), tp('product.2'), tp('product.3'))}`
            : `${pageInfo?.total ?? 0} ${getNoun(pageInfo?.total ?? 0, tp('product.1'), tp('product.2'), tp('product.3'))}`}
        </TitleWithCount>
      )}

      {loading && <ProductsLoading />}

      {isTablet && (
        <div className={styles['group']}>
          <ButtonProductFilters
            className={styles['root__filter-button']}
            externalFilters={availableFilters}
          />
        </div>
      )}

      <InfiniteScroll
        dataLength={products.length}
        next={fetchNextPage}
        hasMore={hasMore}
        loader={loadingMore ? <ProductsLoading /> : null}
      >
        <section className={cn(styles.root, className)}>
          {products.map((p) => (
            <ProductCard
              key={p?.id}
              className={styles.card}
              data={p}
              listName={`Search: ${searchQuery}`}
              listId="search_results"
            />
          ))}
        </section>
      </InfiniteScroll>
    </div>
  )
}
